import type {
  Company,
  CompanyFile,
  CompanyFileKind,
  CompanyInternalNote,
  CompanyMember,
  CompanyOnboardingResponse,
  CompanyOnboardingUpdate,
  CompanyStatus,
  CompanyType,
  UserRegistrationType,
} from "@brainics/api-contract";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { useAuth } from "../../auth/AuthProvider.js";
import { ApiClientError, authErrorMessage } from "../../lib/api.js";
import {
  createCompany,
  createCompanyFile,
  createCompanyInternalNote,
  createCompanyInvitation,
  decideCompanyModeration,
  getCompanyOnboarding,
  listCompanyFiles,
  listCompanyInternalNotes,
  listCompanyMembers,
  listCompanyModerationQueue,
  submitCompanyModeration,
  updateCompanyMember,
  updateCompanyOnboarding,
} from "../../lib/companyApi.js";

const companyIdStorageKey = "brainics.currentCompanyId";

const companyStatusLabels: Record<CompanyStatus, string> = {
  new: "Новая",
  incomplete: "Не заполнена",
  pending_review: "На модерации",
  verified: "Проверена",
  rejected: "Отклонена",
  blocked: "Заблокирована",
  archived: "В архиве",
};

const companyTypeLabels: Record<CompanyType, string> = {
  client: "Заказчик",
  vendor: "Подрядчик",
};

const requiredFieldLabels: Record<string, string> = {
  displayName: "Название",
  legalName: "Юр. название",
  slug: "Slug",
  description: "Описание",
  industry: "Отрасль",
  contactEmail: "Email",
  countryCode: "Страна",
  city: "Город",
  addressLine: "Адрес",
  taxId: "ИНН / Tax ID",
  registrationNumber: "Рег. номер",
};

const companyFormFields = [
  ["displayName", "Название", "Brainics Client"],
  ["slug", "Slug", "brainics-client"],
  ["legalName", "Юридическое название", "Brainics Client LLC"],
  ["description", "Описание", "Краткое описание компании"],
  ["websiteUrl", "Сайт", "https://example.com"],
  ["industry", "Отрасль", "IT"],
  ["contactEmail", "Контактный email", "owner@example.com"],
  ["contactPhone", "Телефон", "+79990000000"],
  ["countryCode", "Страна, ISO-2", "RU"],
  ["city", "Город", "Москва"],
  ["addressLine", "Адрес", "Адрес компании"],
  ["taxId", "ИНН / Tax ID", "1234567890"],
  ["registrationNumber", "Регистрационный номер", "1234567890"],
] as const;

type CompanyFormField = (typeof companyFormFields)[number][0];
type CompanyFormState = Record<CompanyFormField, string>;

const emptyCompanyForm: CompanyFormState = Object.fromEntries(
  companyFormFields.map(([field]) => [field, ""]),
) as CompanyFormState;

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    if (error.code === "COMPANY_NOT_FOUND") return "Компания не найдена или нет доступа.";
    if (error.code === "COMPANY_SLUG_TAKEN") return "Такой slug компании уже занят.";
    if (error.code === "COMPANY_ONBOARDING_LOCKED") {
      return "Компания сейчас не редактируется в этом статусе.";
    }
    if (error.code === "AUTHENTICATION_REQUIRED") return "Нужно войти в аккаунт.";
    if (error.code === "ACCOUNT_UNAVAILABLE") return "Аккаунт недоступен.";
    if (error.code === "FORBIDDEN" || error.status === 403) return "Недостаточно прав.";
    return error.message;
  }

  return authErrorMessage(error);
}

function isCompanyOwnerRole(registrationType: UserRegistrationType): boolean {
  return registrationType === "client_owner" || registrationType === "vendor_owner";
}

function isPlatformStaff(registrationType: UserRegistrationType): boolean {
  return registrationType === "platform_staff";
}

function normalizeNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function toCreatePayload(form: CompanyFormState) {
  return {
    displayName: form.displayName.trim(),
    slug: form.slug.trim(),
    legalName: normalizeNullable(form.legalName),
    description: normalizeNullable(form.description),
    websiteUrl: normalizeNullable(form.websiteUrl),
    industry: normalizeNullable(form.industry),
    contactEmail: normalizeNullable(form.contactEmail),
    contactPhone: normalizeNullable(form.contactPhone),
    countryCode: normalizeNullable(form.countryCode),
    city: normalizeNullable(form.city),
    addressLine: normalizeNullable(form.addressLine),
    taxId: normalizeNullable(form.taxId),
    registrationNumber: normalizeNullable(form.registrationNumber),
  };
}

function toUpdatePayload(form: CompanyFormState): CompanyOnboardingUpdate {
  return toCreatePayload(form);
}

function formFromCompany(company: Company): CompanyFormState {
  return {
    displayName: company.displayName,
    slug: company.slug,
    legalName: company.legalName ?? "",
    description: company.description ?? "",
    websiteUrl: company.websiteUrl ?? "",
    industry: company.industry ?? "",
    contactEmail: company.contactEmail ?? "",
    contactPhone: company.contactPhone ?? "",
    countryCode: company.countryCode ?? "",
    city: company.city ?? "",
    addressLine: company.addressLine ?? "",
    taxId: company.taxId ?? "",
    registrationNumber: company.registrationNumber ?? "",
  };
}

function useCurrentCompanyId() {
  const [companyId, setCompanyIdState] = useState<string>(() => {
    try {
      return localStorage.getItem(companyIdStorageKey) ?? "";
    } catch {
      return "";
    }
  });

  function setCompanyId(next: string) {
    const trimmed = next.trim();
    setCompanyIdState(trimmed);
    try {
      if (trimmed) localStorage.setItem(companyIdStorageKey, trimmed);
      else localStorage.removeItem(companyIdStorageKey);
    } catch {
      // localStorage can be unavailable in restricted browsers.
    }
  }

  return { companyId, setCompanyId };
}

function StatusBadge({ status }: { status: CompanyStatus }) {
  const className =
    status === "verified"
      ? "bg-emerald-300/10 text-emerald-200"
      : status === "pending_review"
        ? "bg-cyan-300/10 text-cyan-200"
        : status === "rejected" || status === "blocked"
          ? "bg-rose-300/10 text-rose-200"
          : "bg-slate-700/50 text-slate-200";

  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${className}`}>
      {companyStatusLabels[status]}
    </span>
  );
}

function PageShell({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-6xl">
      <p className="text-sm font-semibold text-cyan-300">{eyebrow}</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
      <p className="mt-3 max-w-3xl leading-7 text-slate-400">{description}</p>
      <div className="mt-8">{children}</div>
    </div>
  );
}

function InfoPanel({
  title,
  text,
  tone = "default",
}: {
  title: string;
  text: string;
  tone?: "default" | "danger" | "success";
}) {
  const className =
    tone === "danger"
      ? "border-rose-300/15 bg-rose-300/[0.06] text-rose-100"
      : tone === "success"
        ? "border-emerald-300/15 bg-emerald-300/[0.06] text-emerald-100"
        : "border-white/10 bg-white/[0.035] text-slate-300";

  return (
    <div className={`rounded-2xl border p-5 ${className}`}>
      <p className="font-semibold">{title}</p>
      <p className="mt-2 text-sm leading-6 opacity-80">{text}</p>
    </div>
  );
}

function CompanyIdPanel({
  companyId,
  setCompanyId,
}: {
  companyId: string;
  setCompanyId: (value: string) => void;
}) {
  const [draft, setDraft] = useState(companyId);

  useEffect(() => setDraft(companyId), [companyId]);

  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.025] p-6">
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        Текущая компания
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <input
          className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
          onChange={(event) => setDraft(event.target.value)}
          placeholder="companyId UUID"
          value={draft}
        />
        <button
          className="rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200"
          onClick={() => setCompanyId(draft)}
          type="button"
        >
          Сохранить ID
        </button>
        <button
          className="rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-300 transition hover:bg-white/5"
          onClick={() => {
            setDraft("");
            setCompanyId("");
          }}
          type="button"
        >
          Сбросить
        </button>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-500">
        В этой версии frontend использует текущий companyId из localStorage. После создания компании
        ID сохранится автоматически. Endpoint “моя компания” добавим отдельной backend-задачей.
      </p>
    </section>
  );
}

function CompanyCreateCard({
  onCreated,
}: {
  onCreated: (response: CompanyOnboardingResponse) => void;
}) {
  const [form, setForm] = useState<CompanyFormState>({
    ...emptyCompanyForm,
    displayName: "Моя компания",
    slug: "my-company",
    countryCode: "RU",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateField(field: CompanyFormField, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await createCompany(toCreatePayload(form));
      onCreated(response);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="rounded-3xl border border-cyan-300/15 bg-cyan-300/[0.05] p-6 sm:p-8"
      onSubmit={(event) => void handleSubmit(event)}
    >
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-200">
        Создать компанию
      </p>
      <h2 className="mt-3 text-2xl font-semibold">Новый профиль компании</h2>
      <p className="mt-2 text-sm leading-6 text-slate-400">
        Для старта достаточно названия и slug. Остальные поля можно дозаполнить перед отправкой на
        модерацию.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {companyFormFields.slice(0, 6).map(([field, label, placeholder]) => (
          <label className="block" key={field}>
            <span className="text-sm text-slate-400">{label}</span>
            <input
              className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
              onChange={(event) => updateField(field, event.target.value)}
              placeholder={placeholder}
              value={form[field]}
            />
          </label>
        ))}
      </div>

      {error ? <p className="mt-4 text-sm text-rose-200">{error}</p> : null}

      <button
        className="mt-6 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
        disabled={submitting}
        type="submit"
      >
        {submitting ? "Создаём…" : "Создать компанию"}
      </button>
    </form>
  );
}

function CompanySetupState({
  companyId,
  setCompanyId,
}: {
  companyId: string;
  setCompanyId: (value: string) => void;
}) {
  const auth = useAuth();
  if (!auth.user) return null;

  return (
    <div className="space-y-6">
      <CompanyIdPanel companyId={companyId} setCompanyId={setCompanyId} />
      {isCompanyOwnerRole(auth.user.registrationType) ? (
        <CompanyCreateCard
          onCreated={(response) => {
            setCompanyId(response.company.id);
          }}
        />
      ) : (
        <InfoPanel
          title="Создание компании недоступно"
          text="Этот тип аккаунта не создаёт компанию напрямую. Вставьте companyId компании, где у вас есть membership, или войдите под владельцем компании."
        />
      )}
    </div>
  );
}

function CompanySummary({ response }: { response: CompanyOnboardingResponse }) {
  const { company, completion } = response;

  return (
    <section className="grid gap-4 md:grid-cols-4">
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Тип</p>
        <p className="mt-2 text-lg font-semibold">{companyTypeLabels[company.type]}</p>
      </article>
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Статус</p>
        <div className="mt-2">
          <StatusBadge status={company.status} />
        </div>
      </article>
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Заполнение</p>
        <p className="mt-2 text-lg font-semibold">
          {completion.complete ? "100%" : `${Math.max(0, 11 - completion.missingFields.length)}/11`}
        </p>
      </article>
      <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
        <p className="text-sm text-slate-500">Обновлено</p>
        <p className="mt-2 text-lg font-semibold">{formatDateTime(company.updatedAt)}</p>
      </article>
    </section>
  );
}

function CompanyForm({
  response,
  onSaved,
}: {
  response: CompanyOnboardingResponse;
  onSaved: (next: CompanyOnboardingResponse) => void;
}) {
  const [form, setForm] = useState<CompanyFormState>(() => formFromCompany(response.company));
  const [saving, setSaving] = useState(false);
  const [submittingModeration, setSubmittingModeration] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => setForm(formFromCompany(response.company)), [response.company]);

  const missingFields = response.completion.missingFields;

  function updateField(field: CompanyFormField, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const next = await updateCompanyOnboarding(response.company.id, toUpdatePayload(form));
      onSaved(next);
      setNotice("Профиль компании сохранён.");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitModeration() {
    setSubmittingModeration(true);
    setError(null);
    setNotice(null);

    try {
      const next = await submitCompanyModeration(response.company.id);
      onSaved(next);
      setNotice("Компания отправлена на модерацию.");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSubmittingModeration(false);
    }
  }

  return (
    <form className="space-y-6" onSubmit={(event) => void handleSave(event)}>
      <section className="rounded-3xl border border-white/10 bg-white/[0.025] p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Данные компании
            </p>
            <h2 className="mt-3 text-2xl font-semibold">{response.company.displayName}</h2>
            <p className="mt-2 text-sm text-slate-500">ID: {response.company.id}</p>
          </div>
          <StatusBadge status={response.company.status} />
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {companyFormFields.map(([field, label, placeholder]) => (
            <label className="block" key={field}>
              <span className="text-sm text-slate-400">{label}</span>
              <input
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
                onChange={(event) => updateField(field, event.target.value)}
                placeholder={placeholder}
                value={form[field]}
              />
            </label>
          ))}
        </div>

        {missingFields.length > 0 ? (
          <div className="mt-6 rounded-2xl border border-amber-300/15 bg-amber-300/[0.06] p-5">
            <p className="font-semibold text-amber-100">Нужно дозаполнить</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {missingFields.map((field) => (
                <span
                  className="rounded-full bg-amber-300/10 px-3 py-1 text-xs text-amber-100"
                  key={field}
                >
                  {requiredFieldLabels[field] ?? field}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <InfoPanel
            title="Профиль заполнен"
            text="Компанию можно отправить на модерацию."
            tone="success"
          />
        )}

        {error ? <p className="mt-4 text-sm text-rose-200">{error}</p> : null}
        {notice ? <p className="mt-4 text-sm text-emerald-200">{notice}</p> : null}

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            className="rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
            disabled={saving}
            type="submit"
          >
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
          <button
            className="rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-300 transition hover:bg-white/5 disabled:opacity-60"
            disabled={submittingModeration || !response.completion.complete}
            onClick={() => void handleSubmitModeration()}
            type="button"
          >
            {submittingModeration ? "Отправляем…" : "Отправить на модерацию"}
          </button>
        </div>
      </section>
    </form>
  );
}

function useLoadCompany(companyId: string) {
  const [data, setData] = useState<CompanyOnboardingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    try {
      setData(await getCompanyOnboarding(companyId));
    } catch (requestError) {
      setData(null);
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [companyId]);

  return { data, setData, loading, error, reload: load };
}

export function CompanyProfilePage() {
  const { companyId, setCompanyId } = useCurrentCompanyId();
  const { data, setData, loading, error, reload } = useLoadCompany(companyId);

  return (
    <PageShell
      eyebrow="Компания"
      title="Профиль компании"
      description="Создание, редактирование и отправка компании на модерацию."
    >
      <div className="space-y-6">
        <CompanyIdPanel companyId={companyId} setCompanyId={setCompanyId} />
        {!companyId ? (
          <CompanySetupState companyId={companyId} setCompanyId={setCompanyId} />
        ) : null}

        {companyId && loading ? (
          <InfoPanel title="Загрузка" text="Получаем данные компании…" />
        ) : null}
        {companyId && error ? (
          <div className="space-y-4">
            <InfoPanel title="Не удалось загрузить компанию" text={error} tone="danger" />
            <button
              className="rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-300 transition hover:bg-white/5"
              onClick={() => void reload()}
              type="button"
            >
              Повторить
            </button>
          </div>
        ) : null}

        {data ? (
          <>
            <CompanySummary response={data} />
            <CompanyForm response={data} onSaved={setData} />
          </>
        ) : null}
      </div>
    </PageShell>
  );
}

export function CompanyMembersPage() {
  const { companyId, setCompanyId } = useCurrentCompanyId();
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"client_manager" | "vendor_manager">("client_manager");
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await listCompanyMembers(companyId);
      setMembers(response.items);
    } catch (requestError) {
      setMembers([]);
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [companyId]);

  async function handleInvite(event: FormEvent) {
    event.preventDefault();
    if (!companyId) return;

    setInviting(true);
    setError(null);
    setNotice(null);

    try {
      const invitation = await createCompanyInvitation(companyId, { email, role });
      setEmail("");
      setNotice(`Приглашение создано для ${invitation.email}.`);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setInviting(false);
    }
  }

  async function toggleMember(member: CompanyMember) {
    setError(null);
    setNotice(null);

    try {
      const next = await updateCompanyMember(companyId, member.id, {
        status: member.status === "active" ? "inactive" : "active",
        reason: "Changed from company cabinet frontend",
      });
      setMembers((current) => current.map((item) => (item.id === next.id ? next : item)));
      setNotice("Статус участника обновлён.");
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  return (
    <PageShell
      eyebrow="Компания"
      title="Участники"
      description="Список участников компании и приглашение менеджеров."
    >
      <div className="space-y-6">
        <CompanyIdPanel companyId={companyId} setCompanyId={setCompanyId} />
        {!companyId ? (
          <InfoPanel title="Укажите companyId" text="Для загрузки участников нужен ID компании." />
        ) : null}

        {companyId ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void handleInvite(event)}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Пригласить менеджера
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-[1fr_220px_auto]">
              <input
                className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-700 focus:border-cyan-300/60"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="manager@example.com"
                type="email"
                value={email}
              />
              <select
                className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-cyan-300/60"
                onChange={(event) =>
                  setRole(event.target.value as "client_manager" | "vendor_manager")
                }
                value={role}
              >
                <option value="client_manager">Client manager</option>
                <option value="vendor_manager">Vendor manager</option>
              </select>
              <button
                className="rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
                disabled={inviting || !email}
                type="submit"
              >
                {inviting ? "Создаём…" : "Пригласить"}
              </button>
            </div>
          </form>
        ) : null}

        {loading ? <InfoPanel title="Загрузка" text="Получаем участников…" /> : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        {notice ? <InfoPanel title="Готово" text={notice} tone="success" /> : null}

        <section className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.025]">
          {members.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">Участники пока не загружены.</div>
          ) : (
            members.map((member, index) => (
              <div
                className={`grid gap-4 px-6 py-5 lg:grid-cols-[1fr_180px_150px_auto] ${
                  index === members.length - 1 ? "" : "border-b border-white/10"
                }`}
                key={member.id}
              >
                <div>
                  <p className="font-medium text-slate-100">
                    {member.user.firstName} {member.user.lastName}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">{member.user.email}</p>
                </div>
                <p className="text-sm text-slate-400">{member.role}</p>
                <p className="text-sm text-slate-400">{member.status}</p>
                <button
                  className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-300 transition hover:bg-white/5"
                  onClick={() => void toggleMember(member)}
                  type="button"
                >
                  {member.status === "active" ? "Деактивировать" : "Активировать"}
                </button>
              </div>
            ))
          )}
        </section>
      </div>
    </PageShell>
  );
}

export function CompanyFilesPage() {
  const { companyId, setCompanyId } = useCurrentCompanyId();
  const auth = useAuth();
  const [files, setFiles] = useState<CompanyFile[]>([]);
  const [notes, setNotes] = useState<CompanyInternalNote[]>([]);
  const [kind, setKind] = useState<CompanyFileKind>("document");
  const [originalFilename, setOriginalFilename] = useState("");
  const [mimeType, setMimeType] = useState("application/pdf");
  const [sizeBytes, setSizeBytes] = useState("1024");
  const [storageKey, setStorageKey] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingNote, setCreatingNote] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const staff = auth.user ? isPlatformStaff(auth.user.registrationType) : false;

  async function load() {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    try {
      const [filesResponse, notesResponse] = await Promise.all([
        listCompanyFiles(companyId),
        staff ? listCompanyInternalNotes(companyId) : Promise.resolve({ items: [], total: 0 }),
      ]);
      setFiles(filesResponse.items);
      setNotes(notesResponse.items);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [companyId, staff]);

  async function handleCreateFile(event: FormEvent) {
    event.preventDefault();
    if (!companyId) return;

    setCreating(true);
    setError(null);

    try {
      const file = await createCompanyFile(companyId, {
        kind,
        originalFilename,
        mimeType,
        sizeBytes: Number(sizeBytes),
        storageKey: storageKey || `manual/${Date.now()}-${originalFilename}`,
      });
      setFiles((current) => [file, ...current]);
      setOriginalFilename("");
      setStorageKey("");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateNote(event: FormEvent) {
    event.preventDefault();
    if (!companyId || !noteBody.trim()) return;

    setCreatingNote(true);
    setError(null);

    try {
      const note = await createCompanyInternalNote(companyId, { body: noteBody });
      setNotes((current) => [note, ...current]);
      setNoteBody("");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setCreatingNote(false);
    }
  }

  return (
    <PageShell
      eyebrow="Компания"
      title="Файлы"
      description="Метаданные файлов компании и внутренние заметки для команды Brainics."
    >
      <div className="space-y-6">
        <CompanyIdPanel companyId={companyId} setCompanyId={setCompanyId} />
        {!companyId ? (
          <InfoPanel title="Укажите companyId" text="Для загрузки файлов нужен ID компании." />
        ) : null}

        {companyId ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void handleCreateFile(event)}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Добавить metadata файла
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <select
                className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
                onChange={(event) => setKind(event.target.value as CompanyFileKind)}
                value={kind}
              >
                <option value="document">Document</option>
                <option value="logo">Logo</option>
              </select>
              <input
                className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
                onChange={(event) => setOriginalFilename(event.target.value)}
                placeholder="contract.pdf"
                value={originalFilename}
              />
              <input
                className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
                onChange={(event) => setMimeType(event.target.value)}
                placeholder="application/pdf"
                value={mimeType}
              />
              <input
                className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
                onChange={(event) => setSizeBytes(event.target.value)}
                placeholder="1024"
                type="number"
                value={sizeBytes}
              />
              <input
                className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none md:col-span-2"
                onChange={(event) => setStorageKey(event.target.value)}
                placeholder="storage/key/path"
                value={storageKey}
              />
            </div>
            <button
              className="mt-4 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
              disabled={creating || !originalFilename}
              type="submit"
            >
              {creating ? "Добавляем…" : "Добавить файл"}
            </button>
          </form>
        ) : null}

        {staff && companyId ? (
          <form
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
            onSubmit={(event) => void handleCreateNote(event)}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Внутренняя заметка
            </p>
            <textarea
              className="mt-4 min-h-28 w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
              onChange={(event) => setNoteBody(event.target.value)}
              placeholder="Заметка видна только команде Brainics"
              value={noteBody}
            />
            <button
              className="mt-4 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60"
              disabled={creatingNote || !noteBody.trim()}
              type="submit"
            >
              {creatingNote ? "Сохраняем…" : "Добавить заметку"}
            </button>
          </form>
        ) : null}

        {loading ? <InfoPanel title="Загрузка" text="Получаем файлы…" /> : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}

        <section className="grid gap-5 lg:grid-cols-2">
          <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-6">
            <h2 className="text-xl font-semibold">Файлы</h2>
            <div className="mt-4 space-y-3">
              {files.length === 0 ? (
                <p className="text-sm text-slate-500">Файлы пока не загружены.</p>
              ) : (
                files.map((file) => (
                  <div
                    className="rounded-2xl border border-white/10 bg-slate-950 p-4"
                    key={file.id}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{file.originalFilename}</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {file.kind} · {file.mimeType} · {file.sizeBytes} bytes
                        </p>
                      </div>
                      <span className="rounded-full bg-slate-700/50 px-2.5 py-1 text-xs text-slate-300">
                        {file.archivedAt ? "archived" : "active"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-6">
            <h2 className="text-xl font-semibold">Внутренние заметки</h2>
            <div className="mt-4 space-y-3">
              {!staff ? (
                <p className="text-sm text-slate-500">Заметки доступны только Brainics staff.</p>
              ) : notes.length === 0 ? (
                <p className="text-sm text-slate-500">Заметок пока нет.</p>
              ) : (
                notes.map((note) => (
                  <div
                    className="rounded-2xl border border-white/10 bg-slate-950 p-4"
                    key={note.id}
                  >
                    <p className="text-sm leading-6 text-slate-300">{note.body}</p>
                    <p className="mt-3 text-xs text-slate-600">{formatDateTime(note.createdAt)}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>
    </PageShell>
  );
}

export function CompanyModerationPage() {
  const [status, setStatus] = useState<CompanyStatus>("pending_review");
  const [type, setType] = useState<CompanyType | "">("");
  const [items, setItems] = useState<CompanyOnboardingResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [decisionReason, setDecisionReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);

    try {
      const response = await listCompanyModerationQueue({
        status,
        limit: 50,
        offset: 0,
        ...(type ? { type } : {}),
      });
      setItems(response.items);
    } catch (requestError) {
      setItems([]);
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [status, type]);

  async function decide(companyId: string, decision: "verify" | "reject" | "block" | "archive") {
    setError(null);
    setNotice(null);

    try {
      if (decision === "verify") {
        await decideCompanyModeration(companyId, { decision });
      } else if (decision === "archive" && !decisionReason.trim()) {
        await decideCompanyModeration(companyId, { decision });
      } else {
        await decideCompanyModeration(companyId, {
          decision,
          reason: decisionReason || "Decision from frontend",
        });
      }
      setNotice("Решение применено.");
      await load();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  const totalMissing = useMemo(
    () => items.reduce((sum, item) => sum + item.completion.missingFields.length, 0),
    [items],
  );

  return (
    <PageShell
      eyebrow="Brainics staff"
      title="Модерация компаний"
      description="Очередь компаний на проверку и быстрые решения по статусам."
    >
      <div className="space-y-6">
        <section className="grid gap-4 rounded-3xl border border-white/10 bg-white/[0.025] p-6 md:grid-cols-[180px_180px_1fr_auto]">
          <select
            className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
            onChange={(event) => setStatus(event.target.value as CompanyStatus)}
            value={status}
          >
            {Object.keys(companyStatusLabels).map((item) => (
              <option key={item} value={item}>
                {companyStatusLabels[item as CompanyStatus]}
              </option>
            ))}
          </select>
          <select
            className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
            onChange={(event) => setType(event.target.value as CompanyType | "")}
            value={type}
          >
            <option value="">Все типы</option>
            <option value="client">Заказчик</option>
            <option value="vendor">Подрядчик</option>
          </select>
          <input
            className="rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none"
            onChange={(event) => setDecisionReason(event.target.value)}
            placeholder="Причина для reject/block/archive"
            value={decisionReason}
          />
          <button
            className="rounded-xl border border-white/10 px-5 py-3 text-sm text-slate-300 transition hover:bg-white/5"
            onClick={() => void load()}
            type="button"
          >
            Обновить
          </button>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          <InfoPanel title="В очереди" text={`${items.length} компаний`} />
          <InfoPanel title="Незаполненных полей" text={`${totalMissing}`} />
          <InfoPanel
            title="Фильтр"
            text={`${companyStatusLabels[status]}${type ? ` · ${companyTypeLabels[type]}` : ""}`}
          />
        </section>

        {loading ? <InfoPanel title="Загрузка" text="Получаем очередь модерации…" /> : null}
        {error ? <InfoPanel title="Ошибка" text={error} tone="danger" /> : null}
        {notice ? <InfoPanel title="Готово" text={notice} tone="success" /> : null}

        <section className="space-y-4">
          {items.length === 0 ? (
            <InfoPanel title="Пусто" text="По текущему фильтру компаний нет." />
          ) : (
            items.map(({ company, completion }) => (
              <article
                className="rounded-3xl border border-white/10 bg-white/[0.025] p-6"
                key={company.id}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-semibold">{company.displayName}</h2>
                      <StatusBadge status={company.status} />
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      {companyTypeLabels[company.type]} · {company.slug} · {company.id}
                    </p>
                    <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-400">
                      {company.description || "Описание не заполнено"}
                    </p>
                    {!completion.complete ? (
                      <p className="mt-3 text-sm text-amber-200">
                        Не заполнено полей: {completion.missingFields.length}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="rounded-xl bg-emerald-300 px-4 py-2 text-sm font-semibold text-slate-950"
                      onClick={() => void decide(company.id, "verify")}
                      type="button"
                    >
                      Verify
                    </button>
                    <button
                      className="rounded-xl border border-rose-300/20 px-4 py-2 text-sm text-rose-100"
                      onClick={() => void decide(company.id, "reject")}
                      type="button"
                    >
                      Reject
                    </button>
                    <button
                      className="rounded-xl border border-rose-300/20 px-4 py-2 text-sm text-rose-100"
                      onClick={() => void decide(company.id, "block")}
                      type="button"
                    >
                      Block
                    </button>
                    <button
                      className="rounded-xl border border-white/10 px-4 py-2 text-sm text-slate-300"
                      onClick={() => void decide(company.id, "archive")}
                      type="button"
                    >
                      Archive
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </section>
      </div>
    </PageShell>
  );
}
